// State and fixtures of the « Bilan initial » flow (#/bilan-initial). Session-local DEMO: answers stay in memory while the page
// is open (Commencer / Reprendre keeps the position), a reload returns to « à compléter ». Nothing is collected, uploaded or
// sent. Height and the health / progress-photo consents are NOT duplicated here: they live in the account state
// (compte-state.ts, shared with Mon profil and Paramètres); the weight is written to the shared store read by Mon suivi.
import { useSyncExternalStore } from "react";
import { account } from "./compte-state";
import { DEMO_TODAY } from "./demo-context";

export const STEPS = ["Intro", "Objectifs", "Santé", "Mesures", "Photos", "Habitudes", "Récap"] as const;

export const GOALS = ["Reprendre une activité régulière", "Me sentir mieux au quotidien", "Gagner en force", "Améliorer ma condition physique", "Performer dans mon sport", "Autre"] as const;

export type HealthAnswer = "oui" | "non" | "parler";
export const HEALTH_QUESTIONS = [
  { id: "douleurs", q: "As-tu des douleurs ou des blessures en ce moment ?" },
  { id: "operation", q: "As-tu eu une opération récente ?" },
  { id: "probleme", q: "As-tu un problème de santé connu ?" },
  { id: "traitement", q: "Suis-tu un traitement en ce moment ?" },
  { id: "restriction", q: "T'a-t-on conseillé de limiter l'effort ?" },
  { id: "suivi", q: "As-tu un suivi médical en cours ?" },
] as const;
export type HealthId = typeof HEALTH_QUESTIONS[number]["id"];

/** Extra measures configured by the coach (DEMO configuration: labels and units only, no target, no judgement). */
export const COACH_MEASURES = [
  { id: "tour-taille", label: "Tour de taille", unit: "cm" },
  { id: "tour-hanches", label: "Tour de hanches", unit: "cm" },
] as const;

export const PHOTO_SHOTS = [
  { id: "face", label: "Face" },
  { id: "profil-droit", label: "Profil droit" },
  { id: "profil-gauche", label: "Profil gauche" },
  { id: "dos", label: "Dos" },
] as const;
export type ShotId = typeof PHOTO_SHOTS[number]["id"];

/** Habits: activity options and the brake question are validated texts; sleep, fatigue and meal options are DEMO choices. */
export const HABITS = [
  { id: "activite", q: "Quelle est ton activité physique en dehors du studio ?", options: ["Je ne pratique pas actuellement", "Moins d’une fois par semaine", "1 à 2 fois par semaine", "3 fois par semaine ou plus"] },
  { id: "sommeil", q: "Combien dors-tu en général ?", options: ["Moins de 6 h", "6 à 7 h", "7 à 8 h", "Plus de 8 h"] },
  { id: "fatigue", q: "Comment évalues-tu ta fatigue ou ton stress en ce moment ?", options: ["Faible", "Modéré", "Élevé"] },
  { id: "repas", q: "Tes repas ont-ils un rythme régulier ?", options: ["Plutôt réguliers", "Variables", "Plutôt irréguliers"] },
  { id: "frein", q: "Qu’est-ce qui te freine le plus aujourd’hui ?", options: [] },
] as const;
export type HabitId = typeof HABITS[number]["id"];

export interface Bilan {
  status: "todo" | "draft" | "submitted";
  step: number; sub: number;
  goals: string[]; goalOther: string; priority: string;
  /** "parler" = the member prefers to talk directly: the questions are skipped and NO answer is kept. */
  healthChoice?: "questions" | "parler";
  health: Partial<Record<HealthId, { v: HealthAnswer; detail: string }>>;
  weight?: { kg?: number; skipped?: boolean };
  height?: { skipped?: boolean };
  measures: Record<string, { value?: number; skipped?: boolean }>;
  photoChoice?: "photos" | "passer";
  shots: Partial<Record<ShotId, { url?: string; skipped?: boolean }>>;
  habits: Partial<Record<HabitId, string>>;
  submittedAt?: string;
}

let state: Bilan = { status: "todo", step: 0, sub: 0, goals: [], goalOther: "", priority: "", health: {}, measures: {}, shots: {}, habits: {} };
const listeners = new Set<() => void>();
export const bilan = {
  get: () => state,
  subscribe: (l: () => void) => { listeners.add(l); return () => listeners.delete(l); },
  set: (patch: Partial<Bilan>) => { state = { ...state, ...patch }; listeners.forEach(l => l()); },
};
export const useBilan = () => useSyncExternalStore(bilan.subscribe, bilan.get, bilan.get);

/** Consent revocation (Paramètres or elsewhere) clears what depended on it, wherever the member is: health answers without the
 *  health consent, progress-photo previews (blob URLs released) without the photo consent. Granting again starts from empty. */
function syncConsents() {
  const c = account.get().consents;
  let patch: Partial<Bilan> = {};
  if (!c.sensibles && (state.healthChoice === "questions" || Object.keys(state.health).length))
    patch = { ...patch, health: {}, healthChoice: state.healthChoice === "questions" ? undefined : state.healthChoice };
  if (!c.photos && (state.photoChoice === "photos" || Object.keys(state.shots).length)) {
    Object.values(state.shots).forEach(s => { if (s?.url) URL.revokeObjectURL(s.url); });
    patch = { ...patch, shots: {}, photoChoice: state.photoChoice === "photos" ? undefined : state.photoChoice };
  }
  if (Object.keys(patch).length) bilan.set(patch);
}
account.subscribe(syncConsents);

/** A signal for the coach (human review, never a diagnosis): any « Oui » or « Je préfère en parler directement ». */
export const needsTalk = (b: Bilan) => b.healthChoice === "parler" || Object.values(b.health).some(h => h && h.v !== "non");
/** Demo date of the new bilan (the home and Mon suivi live on Monday 12 October). */
export const BILAN_DATE = DEMO_TODAY;
